import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
import { isExampleResource } from '@gonfalon/analytics/isExampleResource';
import { type AnalyticEventData } from '@gonfalon/analytics/types';
import type { AnalyticAttributes } from '@gonfalon/docs/analytics';
import type { Experiment, Iteration } from '@gonfalon/experiments/types';

export type EnvEvaluationSummaryAttributes = {
  hasEnvEvaluated: boolean;
  envLastEvaluatedDate?: number;
  percentageOfFlagsEvaluated?: number;
};
export type FlagEvaluationSummaryAttributes = {
  hasFlagEvaluated: boolean;
  flagLastEvaluatedDate?: number;
  flagThirtyDayEvaluatedCount?: number;
};

export const trackFilterEvent = createTrackerForCategory('Filter');
export const trackExperimentEvent = createTrackerForCategory('Flag Experiments');
export const trackMABEvent = createTrackerForCategory('Multi-armed Bandits');
export const trackNavigationEvent = createTrackerForCategory('Manage Flag Navigation');
export const trackSidenavEvent = createTrackerForCategory('Sidenav');
export const trackNotificationSettingsEvent = createTrackerForCategory('Notification Settings');
export const getTrackingLocation = (pathname: string) => {
  if (pathname.includes('/segments') && (pathname.endsWith('/settings') || pathname.endsWith('/targeting'))) {
    return 'segment detail';
  }
  if (pathname.includes('/segments')) {
    return 'segment';
  }

  if (pathname.includes('/features') && pathname.endsWith('/targeting')) {
    return 'flag detail';
  }

  if (pathname.includes('/features/compare')) {
    return 'compare flags';
  }

  if (pathname.includes('/features')) {
    return 'feature flags';
  }
};
export const trackExperimentCreationEvent = (details: AnalyticEventData) => {
  trackExperimentEvent('New Flag Experiment Created', details);
};
export const getIterationConfigFromIteration = (iteration: Iteration | undefined) => ({
  cupedEnabled: Boolean(iteration?.cupedEnabled),
  isUsingStratifiedSampling: Boolean(iteration?.covariateInfo),
  reshufflingDisabled: iteration?.canReshuffleTraffic === false,
});

export type IterationStartSource = 'first_start' | 'save_and_start' | 'restart';

export const trackIterationStarted = ({
  experimentKey,
  projectKey,
  environmentKey,
  experimentType,
  source,
  iteration,
}: {
  experimentKey: string;
  projectKey: string;
  environmentKey: string;
  experimentType: string | undefined;
  source: IterationStartSource;
  iteration: Iteration | undefined;
}) => {
  const isMAB = experimentType === 'mab';

  (isMAB ? trackMABEvent : trackExperimentEvent)(`${isMAB ? 'Multi-armed Bandit' : 'Experiment'} iteration started`, {
    experimentKey,
    projectKey,
    environmentKey,
    experimentType,
    source,
    isExampleResource: isExampleResource(experimentKey),
    currentIteration: {
      id: iteration?._id,
      status: iteration?.status,
      createdAt: iteration?.createdAt,
      updatedAt: iteration?.updatedAt,
      ...getIterationConfigFromIteration(iteration),
    },
  });
};
function toEventAssignmentSource(experiment: Experiment) {
  const source = (experiment.draftIteration ?? experiment.currentIteration)?.assignmentDataSource;
  if (!source) {
    return undefined;
  }

  return {
    key: source.key,
    name: source.name,
    experimentId: source.experimentId,
  };
}

export const getExperimentEventData = (experiment: Experiment) => {
  return {
    creationDate: experiment._creationDate,
    environmentKey: experiment.environmentKey,
    methodology: experiment.methodology,
    dataSource: experiment.dataSource,
    assignmentSourceType: experiment.assignmentSourceType,
    assignmentSource: toEventAssignmentSource(experiment),
    currentIteration: {
      id: experiment.currentIteration?._id,
      status: experiment.currentIteration?.status,
      createdAt: experiment.currentIteration?.createdAt,
      updatedAt: experiment.currentIteration?.updatedAt,
      ...getIterationConfigFromIteration(experiment.currentIteration),
    },
    analysisConfig: experiment.analysisConfig,
  };
};

//Please be careful when updating. Locations are used across multiple events.
// This enum is also defined in packages/analytics/src/types.ts.
// Keep both in sync when making changes.
export enum EventLocation {
  FLAG_LIST = 'FlagList',
  FLAG_TARGETING = 'FlagTargeting',
  EXPERIMENT_LIST = 'ExperimentList',
  MEMBER_LIST = 'MemberList',
  METRIC_PAGE = 'MetricPage',
  METRIC_GROUP_PAGE = 'MetricGroupPage',
  //experiments tab on flags
  FLAG_EXPERIMENTS = 'FlagExperiments',
  TEAM_CUSTOM_ROLES = 'TeamCustomRoles',
  TEAMS = 'Teams',
  USER_FLAGS = 'UserFlags',
  FLAG_LINKS = 'FLagLinks',
  EDIT_METRIC = 'EditMetric',
  PAYLOAD_FILTERS = 'PayloadFilters',
  FLAGS_IN_SEGMENT = 'FlagsInSegment',
  TOP_NAV = 'TopNav',
  FLAG_INSIGHTS = 'FlagInsights',
  ACCOUNT_USAGE = 'AccountUsage',
  VIEW_LINKED_RESOURCES = 'ViewLinkedResources',
}

export const trackFeatureUpsells = createTrackerForCategory('Upsell');

export const trackUpsellFeatureViewed = (featureKind: string, analyticAttributes: AnalyticAttributes) =>
  trackFeatureUpsells('Feature Upsell Viewed', { featureKind, ...analyticAttributes });

export const trackUpsellLearnMore = (featureKind: string, analyticAttributes: AnalyticAttributes) =>
  trackFeatureUpsells('Upsell Learn More Clicked', { featureKind, ...analyticAttributes });

export const trackUpsellCtaClicked = (
  featureKind: string,
  cta: string,
  analyticAttributes: AnalyticAttributes,
  copy?: string,
) => trackFeatureUpsells('Upsell CTA Clicked', { featureKind, cta, copy, ...analyticAttributes });

export const trackIntegrationEvent = createTrackerForCategory('Integration');

export const trackFlagLinksToolbarEvent = createTrackerForCategory('FlagLinksToolbar');

export type GetComponentAnalyticsArgs = {
  component: string;
  cta?: string;
  type: string;
};

export const getComponentAnalytics = ({ component, cta, type }: GetComponentAnalyticsArgs): AnalyticAttributes => ({
  component,
  url: window.location.href,
  path: window.location.pathname,
  type,
  cta,
});

const trackKeyPressEvent = createTrackerForCategory('KeyPress');
export const trackOnPageSearchEvent = () => {
  document.addEventListener('keydown', (event) => {
    if (!((event.metaKey || event.ctrlKey) && event.key === 'f')) {
      return;
    }

    const { pathname } = document.location;
    if (pathname.match(/settings\/projects$/)) {
      trackKeyPressEvent('Projects Tab - Command + F Pressed');
    } else if (pathname.match(/settings\/projects\/.*\/environments$/)) {
      trackKeyPressEvent('Environments Tab - Command + F Pressed');
    }
  });
};

const trackPlanFeaturesDrawer = createTrackerForCategory('PlanFeaturesDrawer');

export const trackPlanFeaturesDrawerCtaClicked = (analyticAttributes: AnalyticAttributes) =>
  trackPlanFeaturesDrawer('Plan Features CTA Clicked', analyticAttributes);

export const trackPlanFeaturesDrawerInAppLinkClicked = (analyticAttributes: AnalyticAttributes) =>
  trackPlanFeaturesDrawer('Feature CTA Clicked', analyticAttributes);

const trackFollowFlags = createTrackerForCategory('Follow Flags');

export const trackFollowButtonClicked = (
  analyticAttributes: AnalyticAttributes,
  followFlagsAttributes: { isUserFollowingFlag: boolean; flagKey: string },
) => trackFollowFlags('Follow Button Clicked', { ...analyticAttributes, ...followFlagsAttributes });

// This is here as a backup, in case open branches are importing from analyticsUtils and not from @gonfalon/analytics
// If you're setting up a new tracker, be sure to `import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';`
export { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
